import { observable, runInAction } from 'mobx';
import type { WorkItemViewStore } from './WorkItemViewStore';
import { StoreNotLoadedError, type IStoreSlot } from './lazyStoreSlot';
import { workItemSearchQuery } from './workItemSearchQuery';

/**
 * T-0892 (a): where the work-item list's view state lives, so the launch entry does not carry it. Built by
 * `workItemViewStoreInstance.ts`, which every screen that reads `useStores().workItemViewStore` imports.
 *
 * Unlike the other slots (`lazyStores.ts`), its `peek()` is observable: a link drawn before the items pages
 * have loaded (`useItemHref`, in the entry) reads the list's type and full-page choice from it and is drawn
 * again once the store is in.
 */
const held = observable.box<WorkItemViewStore | undefined>(undefined, { deep: false });
let loading: Promise<WorkItemViewStore> | null = null;

export const workItemViewStoreSlot: IStoreSlot<WorkItemViewStore> = {
  provide(store) {
    runInAction(() => held.set(store));
  },
  peek: () => held.get(),
  load() {
    const store = held.get();
    if (store) return Promise.resolve(store);
    loading ??= import('./workItemViewStoreInstance')
      .then((module) => module.workItemViewStore)
      .catch((error: unknown) => {
        // A failed load (a deployment replaced under the tab) is tried again by the next caller.
        loading = null;
        throw error;
      });
    return loading;
  },
  orThrow() {
    const store = held.get();
    if (!store) throw new StoreNotLoadedError('work-item view store');
    return store;
  },
};

/**
 * The search field's write: through the view store when the list has loaded it (which also clears the
 * list's cursor and replaces the address on the next sync), else into the shared query alone.
 */
export function setWorkItemSearchQuery(query: string): void {
  const store = held.get();
  if (store) store.setSearchQuery(query);
  else workItemSearchQuery.set(query);
}
